/**
 * Real-time WebSocket hook for family events.
 *
 * Connects to `wss://ws.kidzio.io/` with the bearer token as a subprotocol
 * (`["kidzio.bearer", "<jwt>"]`). Reconnects with exponential backoff.
 * Calls `onEvent` for each server event AFTER the initial `hello` frame.
 */
import { useEffect, useRef, useState } from 'react';
import { getAccessToken } from '@/auth/tokenStore';

export type KidzioEvent =
  | { type: 'hello'; family_id: string; channels: string[] }
  | { type: 'pong' }
  | {
      type: 'location.updated';
      family_id: string;
      data: {
        child_id: string;
        device_id: string;
        lat: number;
        lng: number;
        accuracy_m: number | null;
        speed_mps: number | null;
        bearing_deg: number | null;
        battery_pct: number | null;
        activity: string | null;
        reported_at: string;
      };
    }
  | {
      type: 'sos.triggered';
      family_id: string;
      data: { sos_id: string; child_id: string; lat: number | null; lng: number | null };
    }
  | {
      type: 'geofence.entered' | 'geofence.exited';
      family_id: string;
      data: { child_id: string; geofence_id: string; geofence_name: string; lat: number; lng: number };
    }
  | {
      // Someone scanned a child's Safe ID emergency tag. `with_location`
      // is true when the finder also consented to share their position —
      // the banner can deep-link to the approximate coords.
      type: 'safeid.scanned';
      family_id: string;
      data: { child_id: string; token: string; with_location: boolean };
    }
  | {
      // Someone scanned a pet's finder tag — same shape as the Safe ID
      // event but keyed on `pet_id`.
      type: 'pet.qr_scanned';
      family_id: string;
      data: { pet_id: string; with_location?: boolean };
    };

export type ConnectionState = 'connecting' | 'open' | 'closed';

const WS_BASE = import.meta.env.VITE_WS_BASE ?? 'wss://ws.kidzio.io';
const HEARTBEAT_MS = 25_000;
const MAX_BACKOFF_MS = 15_000;

export function useFamilyEvents(onEvent: (evt: KidzioEvent) => void): ConnectionState {
  const [state, setState] = useState<ConnectionState>('connecting');
  const callbackRef = useRef(onEvent);
  callbackRef.current = onEvent;

  useEffect(() => {
    let ws: WebSocket | null = null;
    let heartbeat: ReturnType<typeof setInterval> | null = null;
    let reconnect: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;
    let cancelled = false;

    function clearHeartbeat() {
      if (heartbeat) { clearInterval(heartbeat); heartbeat = null; }
    }

    function scheduleReconnect() {
      if (cancelled) return;
      const delay = Math.min(500 * 2 ** attempt, MAX_BACKOFF_MS) + Math.random() * 250;
      attempt += 1;
      reconnect = setTimeout(connect, delay);
    }

    function connect() {
      const token = getAccessToken();
      if (!token) {
        setState('closed');
        scheduleReconnect();
        return;
      }
      setState('connecting');
      try {
        ws = new WebSocket(WS_BASE.replace(/\/+$/, '') + '/', ['kidzio.bearer', token]);
      } catch {
        scheduleReconnect();
        return;
      }
      ws.onopen = () => {
        attempt = 0;
        setState('open');
        heartbeat = setInterval(() => {
          try { ws?.send('{"type":"ping"}'); } catch { /* socket closing */ }
        }, HEARTBEAT_MS);
      };
      ws.onmessage = (e) => {
        try {
          const evt = JSON.parse(e.data) as KidzioEvent;
          callbackRef.current(evt);
        } catch {
          // ignore malformed frames
        }
      };
      ws.onerror = () => { /* onclose follows */ };
      ws.onclose = () => {
        clearHeartbeat();
        setState('closed');
        if (!cancelled) scheduleReconnect();
      };
    }

    connect();
    return () => {
      cancelled = true;
      clearHeartbeat();
      if (reconnect) clearTimeout(reconnect);
      if (ws && ws.readyState <= WebSocket.OPEN) ws.close(1000, 'unmount');
    };
  }, []);

  return state;
}
